/*
 *   File : list.js
 *   Author : https://evoqins.com
 *   Description : Sub admins listing page
 *   Integrations : 'react-toastify'
 *   Version : 1.0.0
*/

// custom packages
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'bootstrap';
import { toast } from 'react-toastify';

// custom components
import { Chip, CustomTab } from '../../Components/Others';
import { PrimaryButton, TextButton } from '../../Components/Buttons';
import { CustomDataTable } from '../../Components/DataTable';
import { AddSubAdminModal, ConfirmModal, ManageRolesModal } from '../../Components/Modal';
import { APIService } from '../../Service';
import EmptyScreen from '../../Components/Others/empty-screen';

const TAB_TITLES = [
    { id: 1, label: 'Sub admins' },
    { id: 2, label: 'Manage roles' },
];

const TOGGLE_DATA = [
    {
        id: 1,
        label: "Active"
    },
    {
        id: 2,
        label: "Inactive"
    }
];

const ITEMS_PER_PAGE = 10;

const SubAdminList = () => {

    const { state } = useLocation();
    const navigate = useNavigate();
    const isRendered = useRef(true);

    const [selectedTab, setSelectedTab] = useState(0);
    const [toggle, setToggle] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const [roleId, setRoleId] = useState(null);
    const [editRole, setEditRole] = useState(null);
    const [admin, setAdmin] = useState(null);

    const [addSubAdmin, setAddSubAdmin] = useState(false);
    const [manageRoleModal, setManageRoleModal] = useState(false);
    const [loader, setLoader] = useState(true);
    const [listLoader, setListLoader] = useState(true);
    const [isPageChanged, setIsPageChanged] = useState(false);
    const [confirm, setConfirm] = useState(false);
    const [status, setStatus] = useState(false);

    const [manageRoles, setManageRoles] = useState({});
    const [roles, setRoles] = useState([]);
    const [subAdmins, setSubAdmins] = useState({});

    useEffect(() => {
        window.scrollTo({
            top: 0, behavior: 'smooth'
        });

        if (selectedTab === 2) {
            _manageRoles();
        }
    }, [selectedTab]);

    useEffect(() => {
        if (toggle !== 0 && selectedTab === 1) {
            _listSubAdmins();
        }
    }, [selectedTab, toggle]);

    useEffect(() => {
        if (state !== null) {
            setSelectedTab(state.tab_index);
            if (state.toggle) {
                setToggle(state.toggle);
            } else {
                setToggle(1);
            }
        } else {
            setSelectedTab(1);
            setToggle(1);
        }
    }, [state]);

    useEffect(() => {
        if (addSubAdmin === true) {
            const open_modal = new Modal(document.getElementById('add-sub-admin-modal'));
            open_modal.show();
        }
    }, [addSubAdmin]);

    useEffect(() => {
        if (manageRoleModal === true) {
            const open_modal = new Modal(document.getElementById('manage-role-modal'));
            open_modal.show();
        }
    }, [manageRoleModal]);


    useEffect(() => {
        if (confirm === true) {
            const confirm_modal = new Modal(document.getElementById('confirm-modal'));
            confirm_modal.show();
        }
    }, [confirm]);


    useEffect(() => {
        _handleBackdrop();
        // Add the event listener
        window.addEventListener('popstate', _handleBackdrop);
        // Cleanup the event listener on component unmount
        return () => {
            window.removeEventListener('popstate', _handleBackdrop);
        };
        //eslint-disable-next-line
    }, [selectedTab]);

    // handle back browser
    function _handleBackdrop() {
        setAddSubAdmin(false);
        if (document.querySelector(".modal-backdrop")) {
            document.querySelector(".modal-backdrop").remove();
            document.body.style.overflow = 'unset';
        };
    };

    // handle tab change
    const _handleTab = (tab_id) => {
        isRendered.current = false;
        navigate("/sub-admins", {
            state: {
                tab_index: tab_id,
                toggle: toggle
            }
        });
    };

    // handle toggle  
    const _handleToggle = (id, label) => {
        // setToggle(id);
        // _listSubAdmins(currentPage, id);
        setListLoader(true);
        setCurrentPage(1);
        navigate("/sub-admins", {
            state: {
                tab_index: 1,
                toggle: id
            }
        });
    };

    // handle add sub admin 
    const _handleSubAdmin = () => {
        setAddSubAdmin(true);
    };

    // handle table page change 
    const _handleTablePageChange = (page_num) => {
        setCurrentPage(page_num);

        if (selectedTab === 1) {
            _listSubAdmins(page_num);
        } else {
            _manageRoles(page_num);
        }

        window.scrollTo({
            top: 0, behavior: 'smooth'
        });

    };

    // handle manage role
    const _handleManageRole = (type, data = null) => {
        if (type === 1) {
            setManageRoleModal(true);
        } else {
            setManageRoleModal(true);
            setEditRole(data);
        }
    };

    // handle navigate admin detail
    const _handleAdminDetail = (id) => {
        navigate(`${id}`);
    };

    // handle remove role
    const _handleRemoveRole = (role_id) => {
        setConfirm(true);
        setRoleId(role_id)
    };

    // Admins table column 
    const ADMIN_TABLE_COLUMN = [
        {
            name: 'Name',
            cell: (row) =>
                <span className='text-truncate e-text-blue-violet text-decoration-hover-underline cursor-pointer'
                    onClick={() => _handleAdminDetail(row.admin_id)}>
                    {row.username}
                </span>,
            width: "250px"
        },
        {
            name: 'Email',
            cell: (row) =>
                <a href={`mailto:${row.email}`} target='_blank' className='text-truncate e-text-smoky-black text-decoration-none text-decoration-hover-underline'>
                    {row.email}
                </a>,
            width: "280px"
        },
        {
            name: 'Phone number',
            cell: (row) => <div>
                {
                    row.mobile_number !== null ?
                        <a href={`tel:${row.mobile_number}`} className='e-text-smoky-black text-decoration-none text-decoration-hover-underline'>
                            {row.mobile_number}
                        </a>
                        :
                        <span>-</span>
                }
            </div>,
            width: "180px"
        },
        {
            name: 'Roles',
            selector: row => row.role,
            width: "160px"
        },
        {
            name: 'Modules',
            cell: (row) =>
                <p className='mb-0 truncate-third-lin line-height-24px'>
                    {row.modules.map((module, index) => (
                        <span key={module.module_id} className='d-inline-flex'>{module.name}
                            {index < row.modules.length - 1 && ' , '} </span>
                    ))}
                </p>,
            width: "400px"
        },
        {
            name: '',
            cell: (row) =>
                <Chip label={row.is_active === true ? "Active" : "Inactive"}
                    className={row.is_active === true ? "e-bg-green-crayola" : "e-bg-taupe-gray"} />,
            width: "120px"
        },
    ];

    // role list table column 
    const ROLE_TABLE_COLUMN = [
        {
            name: 'Roles',
            selector: row => row.name,
            width: "160px"
        },
        {
            name: 'Modules',
            cell: (row) =>
                <p className='mb-0 truncate-third-lin line-height-24px'>
                    {row.modules.map((module, index) => (
                        <span key={module.module_id} className='d-inline-flex'>{module.name}
                            {index < row.modules.length - 1 && ' , '} </span>
                    ))}
                </p>
            ,
            width: "450px"
        },
        {
            name: '',
            cell: (row) =>
                <TextButton name="Edit"
                    className='text-decoration-hover-underline'
                    onClick={() => _handleManageRole(2, row)} />,
            width: "90px"
        },
        {
            name: '',
            cell: (row) =>
                <TextButton name="Remove"
                    className='text-decoration-hover-underline'
                    onClick={() =>
                        _handleRemoveRole(row.id)
                    } />,
            width: "120px"
        },
    ];


    // API - Sub-admins  (manage roles)
    const _manageRoles = (page_num = currentPage) => {
        let is_post = true;
        let url = 'sub-admin/role/list';
        let request = {
            "page_num": page_num,
            "page_size": ITEMS_PER_PAGE,
            "query": ""
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setManageRoles(response.data);
                const roles = []

                response.data.data.map((role) => {
                    return roles.push({
                        label: role.name,
                        value: role.id
                    })
                })
                setRoles(roles);
            } else {
                setManageRoles({ data: [] });
            }
            setLoader(false);
            setIsPageChanged(false);
        })
    };


    // API - Sub-admins  (list)
    const _listSubAdmins = (page_num = currentPage, is_active = toggle) => {
        let is_post = true;
        let url = 'sub-admin/list';
        let request = {
            "page_num": page_num,
            "page_size": ITEMS_PER_PAGE,
            "is_active": is_active === 1 ? true : false,
            "query": ""
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {
                setSubAdmins(response.data);
                const admins = response.data.data.map((admin) => ({
                    label: admin.username,
                    value: admin.admin_id
                }));
                setAdmin(admins);
            } else {
                setSubAdmins({ data: [] });
            }
            setListLoader(false);
        })
    };

    const [removeLoader, setRemoveLoader] = useState(false);

    // API - remove admin
    const _removeAdmin = (role_id) => {
        setRemoveLoader(true);
        let is_post = true;
        let url = 'sub-admin/roles/delete';
        let request = {
            "role_id": role_id
        }
        APIService(is_post, url, request).then((response) => {
            if (response.status_code === 200) {

                setStatus(true);
            } else {
                toast.dismiss();
                toast.error(response.message, { type: "error" })
            }
            setRemoveLoader(false);
        })
    };


    return (
        <Fragment>
            <section className='e-section-height pb-5' >
                <div className='e-bg-lavender padding-52px-lr pt-2 padding-112px-bottom'>
                    <h3 className='mb-0 e-text-chinese-black e-primary-font e-font-24 e-font-weight-600 line-height-24px'>
                        Manage Sub Admins
                    </h3>
                </div>

                <div className='padding-52px-lr margin--80px-top'>
                    <div className='e-bg-white border-radius-16 '>
                        <div className='padding-32px-top'>
                            {/* Custom tab */}
                            <div className='d-flex padding-32px-lr '>
                                <CustomTab data={TAB_TITLES}
                                    selectedTab={selectedTab}
                                    tooltip={false}
                                    handleClick={_handleTab} />
                            </div>

                            {/* Sub admins list */}
                            {selectedTab === 1 ?
                                <div>
                                    <div className='padding-32px-lr d-flex justify-content-between pt-4 align-items-center'>
                                        <h4 className='mb-0 e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                                            Sub admins list
                                        </h4>

                                        <div className='d-flex align-items-center gap-44px'>
                                            <div className='d-flex e-border-0-5px-blue-violet p-1 border-radius-16'>
                                                {TOGGLE_DATA.map((data, key) => (
                                                    <span key={key} className={`${toggle === data.id ? 'pe-none e-bg-pale-lavender-dark' : 'bg-transparent cursor-pointer'} e-text-eerie-black e-primary-font e-font-14 e-font-weight-400 line-height-18px px-1 border-radius-100`}
                                                        onClick={() => _handleToggle(data.id, data.label)}>
                                                        {data.label}
                                                    </span>
                                                ))}
                                            </div>

                                            <PrimaryButton name="Create Sub Admin"
                                                paddingBlock="padding-12px-tb px-3"
                                                onClick={_handleSubAdmin} />
                                        </div>
                                    </div>

                                    {/* Sub admins data table */}
                                    {listLoader ?
                                        <div className='d-flex align-items-center justify-content-center min-vh-100'>
                                            <img src={require("../../Assets/Image/loader.gif")}
                                                draggable={false}
                                                width={80}
                                                height={80}
                                                alt='loader'
                                                className='text-center' />
                                        </div>
                                        :
                                        subAdmins.data.length > 0 ?
                                            <div className='e-data-table-wrapper-default-updated pt-3 pb-4'>
                                                <CustomDataTable columns={ADMIN_TABLE_COLUMN}
                                                    data={subAdmins.data}
                                                    pagination={true}
                                                    paginationPerPage={ITEMS_PER_PAGE}
                                                    lastPage={subAdmins.total_page}
                                                    paginationDefaultPage={subAdmins.current_page}
                                                    paginationTotalRows={subAdmins.total_count}
                                                    onChangePage={_handleTablePageChange}
                                                // rowClick={(row) => _handleAdminDetail(row.admin_id)}
                                                />
                                            </div>
                                            :
                                            <EmptyScreen
                                                image={require('../../Assets/Image/NotFound/no-data.png')}
                                                title='No results found'
                                                className="min-vh-50 d-flex align-items-center justify-content-center flex-column"
                                                description={`We couldn' t find anything that matches your search. Try checking your spelling, using different keywords, or exploring popular funds.`}
                                            />
                                    }
                                </div>
                                :
                                // Roles list 
                                <div>
                                    <div className='padding-32px-lr d-flex justify-content-between pt-4 align-items-center'>
                                        <h4 className='mb-0 e-text-smoky-black e-primary-font e-font-16 e-font-weight-600 line-height-20px'>
                                            Roles list
                                        </h4>

                                        <PrimaryButton name="Create New Role"
                                            paddingBlock="padding-12px-tb padding-20px-lr"
                                            onClick={() => _handleManageRole(1)} />
                                    </div>

                                    {/* ROLE list table */}
                                    {loader ?
                                        <div className='d-flex align-items-center justify-content-center min-vh-100'>
                                            <img src={require("../../Assets/Image/loader.gif")}
                                                draggable={false}
                                                width={80}
                                                height={80}
                                                alt='loader'
                                                className='text-center' />
                                        </div>
                                        :
                                        manageRoles.data?.length > 0 ?
                                            <div className='e-data-table-wrapper-default-updated pt-3 padding-32px-bottom'>
                                                <CustomDataTable columns={ROLE_TABLE_COLUMN}
                                                    data={manageRoles.data}
                                                    pagination={true}
                                                    paginationPerPage={ITEMS_PER_PAGE}
                                                    lastPage={manageRoles.total_page}
                                                    paginationDefaultPage={manageRoles.current_page}
                                                    paginationTotalRows={manageRoles.total_count}
                                                    onChangePage={_handleTablePageChange} />
                                            </div>
                                            :
                                            <EmptyScreen
                                                image={require('../../Assets/Image/NotFound/no-data.png')}
                                                title='No results found'
                                                description={`We couldn' t find anything that matches your search. Try checking your spelling, using different keywords, or exploring popular funds.`}
                                            />
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
            </section>

            {/* Add sub admin modal  */}
            {addSubAdmin === true &&
                <AddSubAdminModal roles={roles}
                    admins={admin}
                    handleClose={() => {
                        setTimeout(() => {
                            setAddSubAdmin(false);
                            _listSubAdmins();
                        }, 200);
                    }} />
            }

            {/* Mange role modal  */}
            {manageRoleModal === true &&
                <ManageRolesModal edit={editRole}
                    roleAdded={() => _manageRoles()}
                    roleEdited={() => _manageRoles()}
                    handleClose={() => {
                        setTimeout(() => {
                            setManageRoleModal(false);
                            setEditRole(null);
                        }, 200);
                    }} />
            }

            {/* confirm remove role modal */}
            {confirm === true &&
                <ConfirmModal title={status ? "Confirm successfully" : "Confirm role deletion"}
                    description={status ? "The role has been deleted successfully." : "Are you sure you want to remove this role? This action cannot be undone."}
                    status={status}
                    loader={removeLoader}
                    handleClose={() => {
                        setTimeout(() => {
                            setConfirm(false);
                            setStatus(false);
                        }, 200);
                    }}
                    handleConfirm={() => {
                        _removeAdmin(roleId)
                    }
                    }
                    handleSuccess={() => {
                        setTimeout(() => {
                            setLoader(true);
                            setConfirm(false);
                            _manageRoles();
                            setStatus(false);
                        }, 200);
                    }} />
            }
        </Fragment>
    )
}

export default SubAdminList;
